import classNames from 'classnames';
// import 'prismjs/components/prism-json';
// import 'prismjs/components/prism-sql';
// import { highlight } from 'prismjs/components/prism-core';
import React, {
  PropsWithChildren,
  ReactNode,
  useLayoutEffect,
  useRef,
} from 'react';
import { cn } from '../common/utils';
import CopyToClipboardButton from '../components/actions/CopyToClipboardButton';
import CodeEditor, { CodeEditorRef } from '../components/editors/CodeEditor';
import { ActionBar } from '../components/nav/ActionBar';

export type CodeSnippetProps = PropsWithChildren & {
  className?: string;
  title?: string;
  code?: string;
  children?: ReactNode;
  canCopy?: boolean;
};

// Improved function to convert children to a string recursively while preserving whitespace
const convertChildrenToString = (children: ReactNode): string => {
  if (typeof children === 'string') {
    return children;
  }

  if (React.isValidElement(children)) {
    if ('props' in children) {
      if (children.props.originalMarkdown) {
        return children.props.originalMarkdown;
      }
      if (children.props.raw) {
        return children.props.raw;
      }
      return convertChildrenToString(children.props.children);
    }
  }

  if (Array.isArray(children)) {
    return children.map(child => convertChildrenToString(child)).join('');
  }

  return '';
};

const CodeSnippet: React.FC<CodeSnippetProps> = props => {
  const codeString = convertChildrenToString(props.children);
  const editorRef = useRef<CodeEditorRef>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const CSS_MIN_HEIGHT_PX = 120; // Approx 5-6 lines
  const CSS_MAX_HEIGHT_PX = 400; // Max height before scrolling

  // No more useLayoutEffect for height calculation or editorHeight state

  // Optional: A simple effect to call editor.layout() on content change or resize,
  // might help Monaco adjust to its container if its size changes due to external factors.
  useLayoutEffect(() => {
    const editor = editorRef.current?.getEditor();
    const container = editorContainerRef.current;
    if (!editor || !container) return;

    const observer = new ResizeObserver(() => {
      editor.layout();
    });
    observer.observe(container);

    // Also layout once when code string changes and editor is available
    editor.layout();

    return () => {
      observer.disconnect();
    };
  }, [codeString]); // Only re-run if codeString changes, or on mount

  return (
    <>
      {(props.title || props.canCopy) && (
        <ActionBar
          className={cn('bg-app-panel-dark/20', 'px-2', 'py-1', 'rounded-t-sm')}
        >
          <div className="text-xxs flex-1">{props.title}</div>
          {props.canCopy && (
            <CopyToClipboardButton hasIconSmall value={codeString} />
          )}
        </ActionBar>
      )}

      <div
        ref={editorContainerRef}
        data-component="CodeSnippet"
        className={classNames(
          'font-mono text-xs',
          'bg-app-panel-dark/60',
          'text-menu-text/60',
          'text-left',
          // 'whitespace-pre-wrap', // Monaco handles its own whitespace and wrapping
          'select-text',
          'p-0.5',
          'w-full',
          'rounded-b-sm',
          'mb-2',
          // overflow-hidden is removed from here, will be handled by inline style or Monaco itself
          'flex flex-col', // Allows editor to grow
          props.className,
        )}
        style={{
          minHeight: `${CSS_MIN_HEIGHT_PX}px`,
          maxHeight: `${CSS_MAX_HEIGHT_PX}px`,
          overflowY: 'auto', // Ensures scrollbar appears if content exceeds maxHeight
        }}
      >
        <CodeEditor
          value={codeString}
          language={'trinosql'}
          height="100%" // Tells Monaco to fill its parent (this div)
          ref={editorRef}
          editorOptions={{
            readOnly: true,
            scrollBeyondLastLine: false,
            overviewRulerBorder: false,
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            lineDecorationsWidth: 10,
            scrollbar: {
              vertical: 'auto', // Monaco handles its own scrollbar if needed within its 100% height
              horizontal: 'hidden',
              handleMouseWheel: true,
            },
            guides: {
              indentation: false,
              highlightActiveIndentation: true,
            },
            fontSize: 12,
            lineNumbers: 'off',
            folding: false,
            tabSize: 2,
            padding: { top: 10, bottom: 10 },
            wordWrap: 'on',
          }}
        />
      </div>
    </>
  );
};

export default CodeSnippet;
